/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import { Box } from "@nordcloud/gnui";
import {
  BreadcrumbsBox,
  BrickLoader,
  DataDisplayWrapper,
  NoData,
  UniversalWrap,
} from "~/components";
import { useLexicalTabsWithUrl, useQueryState, useToggle } from "~/hooks";
import { getFirstItem, isNil, isNotEmpty } from "~/tools";
import { CloudAccountsFilterSidebar } from "./CloudAccountList/CloudAccountsFilterSidebar";
import { CloudAccountsTabs } from "./CloudAccountList/CloudAccountsTabs";
import { staticChargeTypeOptions } from "./CloudAccountList/constants";
import { useCloudAccountsFetch, useCostsByProvider } from "./hooks";
import {
  CloudAccountsQueryState,
  Provider,
  ProviderFilterValues,
  ProviderStatus,
  ProvidersForFiltering,
  SearchSortOrder,
} from "./types";
import { useGetMovedCosts } from "./useGetMovedCosts";

const breadcrumbLabels = [{ value: "cloud-accounts", label: "Cloud Accounts" }];

export function CloudAccountsPage() {
  const { state, updateQueryState } = useQueryState<CloudAccountsQueryState>();
  const { provider, query, status } = state;

  const { data: movedCosts, loading: isLoadingMovedCosts } = useGetMovedCosts(
    query,
    status
  );

  const {
    tabs,
    loading: isLoadingAccountCounts,
    error: accountCountsError,
  } = useCostsByProvider(movedCosts);

  const {
    data,
    loading: isLoadingCloudAccounts,
    error: cloudAccountsError,
  } = useCloudAccountsFetch();

  const { activeTab, setActiveTab, registerTabs } =
    useLexicalTabsWithUrl("provider");

  useEffect(() => {
    registerTabs(tabs.map((tab) => tab.name));
    // missing params will invoke infinite loop, thus disabling exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, isLoadingAccountCounts, isLoadingMovedCosts]);

  useEffect(() => {
    if (
      !isLoadingAccountCounts &&
      !isLoadingMovedCosts &&
      isNil(provider) &&
      isNotEmpty(tabs)
    ) {
      updateQueryState({
        ...state,
        provider: getFirstItem(tabs)?.name as Provider,
      });
    }
    // missing params will invoke infinite loop, thus disabling exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, provider, isLoadingAccountCounts, isLoadingMovedCosts]);

  const [searchInput, setSearchInput] = useState(query);
  const [isFiltersSidebarOpen, toggleIsFiltersSidebarOpen] = useToggle(false);

  const error = accountCountsError || cloudAccountsError;

  const clearFilters = () => {
    updateQueryState({
      ...state,
      status: [],
      providerFilter: [],
      page: 0,
    });
    toggleIsFiltersSidebarOpen();
  };

  const applyFilters = (
    providerStatus: ProviderStatus,
    providerValues: ProviderFilterValues
  ) => {
    const newStatus = Object.entries(providerStatus)
      .filter(([_, isChecked]) => isChecked)
      .map(([statusName]) => statusName);
    const newProviderFilterValues = Object.entries(providerValues)
      .filter(([_, isChecked]) => isChecked)
      .flatMap(([providerValue]) => {
        switch (providerValue) {
          case Provider.Aws:
            return [Provider.Aws];
          case Provider.Azure:
            return [Provider.Azure];
          case Provider.Gcp:
            return [Provider.Gcp];
          default:
            return [];
        }
      });

    updateQueryState({
      ...state,
      status: newStatus,
      providerFilter: newProviderFilterValues as ProvidersForFiltering[],
      page: 0,
    });
    toggleIsFiltersSidebarOpen();
  };

  const onTabChange = (tab: number) => {
    setActiveTab(tab, {
      chargeType: getFirstItem(staticChargeTypeOptions).value,
      order: SearchSortOrder.Dsc,
      page: 0,
      providerFilter: [],
    });
  };

  return (
    <>
      <BreadcrumbsBox title="Cloud Accounts" labels={breadcrumbLabels} />
      <UniversalWrap
        errorProps={{ error }}
        loaderProps={{
          loading: isLoadingAccountCounts || isLoadingMovedCosts,
          Component: BrickLoader,
        }}
      >
        <DataDisplayWrapper
          hasData={isNotEmpty(tabs)}
          renderNoData={(message) => (
            <Box>
              <NoData message={message} />
            </Box>
          )}
        >
          <CloudAccountsTabs
            activeTab={activeTab}
            tabs={tabs}
            openFiltersSidebar={toggleIsFiltersSidebarOpen}
            isLoadingCloudAccounts={isLoadingCloudAccounts}
            searchInput={searchInput}
            data={data}
            setActiveTab={onTabChange}
            onSetSearchInput={setSearchInput}
            onClear={clearFilters}
          />
        </DataDisplayWrapper>
      </UniversalWrap>
      <CloudAccountsFilterSidebar
        isFiltersSidebarOpen={isFiltersSidebarOpen}
        onClearFilters={clearFilters}
        onApplyFilters={applyFilters}
        onHandleClose={toggleIsFiltersSidebarOpen}
      />
    </>
  );
}
